// CERTIFICATION QUEUE — one component, used by RULING 22 (sales invoices, mig 346) and RULING 23
// (debit notes, mig 351).
//
// WHY PARAMETERISED RATHER THAN COPIED. The two screens are the same screen: a queue that shrinks, a
// per-viewer refusal reason, four tiles, and the handover gap above them. A second copy would have
// been quicker to write and would have drifted the first time one of the rulings moved -- which is
// the argument this session has made about the rule living in one place, and it applies to the
// surface that displays the rule too. `config` carries the endpoint, the nouns and the columns; NO
// behaviour is configurable, because a screen whose behaviour varies by config is two screens again.
//
// THE ONE DESIGN DECISION THAT MATTERS HERE. Migration 346 refuses a certification by the person who
// raised the invoice, compared through effective identity. So on this screen a row is frequently NOT
// actionable by the person looking at it — and the wrong way to show that is a greyed button, because
// a disabled control with no explanation is how people conclude the system is broken.
//
// Every blocked row therefore states its reason IN PLACE OF the buttons, in the same words the
// database uses, and says it is a staffing requirement rather than a fault. The route computes
// can_certify per viewer through effective_user_id() — the same function the trigger calls — so this
// screen can never offer a button the database will refuse.
//
// AND THE GAP IS ON THE SCREEN (§9z rule 3). Whatever the queue excludes because it predates the
// handover is shown above the queue, not hidden. A gap nobody can see is indistinguishable from a
// gap nobody has.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { askReason } from '../utils/reportError';

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const inr = (v) => '₹' + num(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';

const TH = { textAlign: 'left', padding: '9px 10px', fontSize: 11, fontWeight: 700, color: '#666',
             textTransform: 'uppercase', letterSpacing: '.03em', borderBottom: '1px solid #E8E7E7', whiteSpace: 'nowrap' };
const TD = { padding: '10px', fontSize: 13, borderBottom: '1px solid #F2F1F1', verticalAlign: 'top' };

export default function CertificationQueue({ config }) {
  const C = config;
  // THE WORDS AND THE ONE QUESTION ARE DATA; THE BEHAVIOUR IS NOT. The gate-pass declaration queue
  // (mig 353) is the same screen as the two certification queues — a shrinking queue, a per-viewer
  // refusal in the database's words — but its act is "declare", and that act carries a REASON the
  // database requires. So a config may name its verb and ask its approval question; everything else,
  // including who may act, stays here. Omitted, both default to exactly what sales and debit notes had.
  const W = { verb: 'certify', Verb: 'Certify', act: 'Certification', acted: 'certified', ...(C.words || {}) };
  const [rows, setRows] = useState([]);
  const [gap, setGap] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(() => {
    setLoading(true); setErr('');
    api.get(C.queueUrl)
      .then((r) => { setRows(r.data.data || []); setGap(r.data.gap || []); setTotals(r.data.totals || null); })
      .catch((e) => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
    // C.queueUrl IS in the dependency list. Today both configs are module constants so it never
    // changes, and an empty list would work -- but a dependency omitted because it "cannot" change is
    // a bug waiting for the day it can, and this component now has two callers.
  }, [C.queueUrl]);
  useEffect(load, [load]);

  // THE LIST SHRINKS AS THEY WORK. A queue that keeps a row after it is dealt with is a list, not a
  // queue, and the person loses their place in it. Reload rather than splice: the totals and the gap
  // have to move with it or the denominator goes stale.
  // A config may supply ApproveForm: the approval then opens a FORM (the gate pass declaration asks
  // which evidence was checked — mig 355) instead of a prompt. The form returns the body; posting,
  // reloading and showing the refusal stay here, so the queue is still one component.
  const [formRow, setFormRow] = useState(null);
  const [formErr, setFormErr] = useState('');
  const submitForm = async (body) => {
    setBusy(formRow.id); setFormErr(''); setNote('');
    try {
      const r = await api.post(C.certifyUrl(formRow.id), { decision: 'approved', ...body });
      setNote(C.approvedNote ? C.approvedNote(r.data.data) : `${W.Verb} recorded.`);
      setFormRow(null); load();
    } catch (e) { setFormErr(e.response?.data?.error || e.message); }   // stays open, refusal shown in the form
    finally { setBusy(null); }
  };
  const act = async (row, decision) => {
    let remarks = '';
    if (decision === 'approved' && C.ApproveForm) { setFormErr(''); setFormRow(row); return; }
    if (decision === 'rejected') {
      remarks = window.prompt(
        `Reject ${C.noun} ${C.refOf(row) || '(no number)'} — ${(C.fmtAmount || inr)(C.amountOf(row))}.\n\n`
        + 'Why? The reason is the only part of a refusal the next person can act on, and it is kept '
        + 'verbatim.') || '';
      if (remarks.trim().length < 3) { setNote('Rejection cancelled — no reason given.'); return; }
    } else if (C.approveReason) {
      const why = askReason(C.approveReason(row));
      if (why == null) return;
      remarks = why;
    } else if (!window.confirm(
        `Certify ${C.noun} ${C.refOf(row) || '(no number)'} for ${(C.fmtAmount || inr)(C.amountOf(row))}?\n\n`
        + `Raised by ${row.created_by_name || '(unknown)'}. This records that you checked these `
        + `figures.${C.lockNote || ''}`)) { return; }

    setBusy(row.id); setNote('');
    try {
      const r = await api.post(C.certifyUrl(row.id), { decision, remarks });
      setNote(decision === 'approved'
        ? (C.approvedNote ? C.approvedNote(r.data.data) : `Certified ${C.refOf(r.data.data) || ''} — ${inr(r.data.data.certified_amount)} recorded against your name.`)
        : `Rejected ${C.refOf(r.data.data) || ''}. The reason is recorded on it.`);
      load();
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally { setBusy(null); }
  };

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#1a1a2e' }}>{C.title}</h3>
        <div style={{ fontSize: 12, color: '#777', maxWidth: 780, lineHeight: 1.5 }}>
          {C.blurb} <strong>{W.act} must be a second person's act</strong> — whoever raised
          {' '}{C.noun} cannot {W.verb} it, and {C.aNoun} raised by a <em>shared</em> login cannot be
          {' '}{W.acted} at all, because there is no first person to be second to.
        </div>
      </div>

      {/* PRINT THE DENOMINATOR. "8 you can certify" means something different out of 12 than out of 400. */}
      {totals && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {[[`Awaiting ${W.act.toLowerCase()}`, totals.pending, '#BA7517', null],
            [`You can ${W.verb}`, totals.certifiable_by_viewer, '#1D9E75', null],
            ['You raised — someone else must', totals.blocked_you_raised_it, '#3B3A8F', null],
            // RULING 33. Distinct from the tile above, and it has to be: "somebody else must do it"
            // and "nobody can do it until a person is named" are different problems with different
            // remedies, and one number covering both would send people looking for a spare certifier.
            ['Raised by a shared login — needs a named login', totals.blocked_shared_raiser, '#9A6B00',
             'A shared login is not a person, so certifying these would record that two people '
             + 'looked at them when nobody knows who the first one was.'],
            ['No recorded raiser', totals.no_raiser, '#E22D2D', null]].map(([l, v, c, hint]) => (
            <div key={l} title={hint || undefined}
                 style={{ flex: '1 1 150px', minWidth: 138, border: '1px solid #E8E7E7',
                          borderRadius: 8, padding: '10px 12px', background: '#fff' }}>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 3, lineHeight: 1.3 }}>{l}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: c }}>{num(v)}</div>
            </div>
          ))}
        </div>
      )}

      {/* §9z RULE 3 — what the queue excludes is REPORTED, never hidden. */}
      {gap.length > 0 && (
        <div style={{ border: '1px solid #E8D9B0', background: '#FDF8EC', borderRadius: 8,
                      padding: '11px 13px', marginBottom: 14, fontSize: 12.5, color: '#6B5418', lineHeight: 1.55 }}>
          <strong>Not shown here, and never to be certified:</strong>{' '}
          {gap.map((g) => (
            <span key={g.company_id || 'x'}>
              {g.company_name || '—'} — {num(g.invoices_before_handover)} invoices, {inr(g.value_before_handover)},
              dated before the {fmtD(g.handover_date)} handover ({fmtD(g.earliest)} – {fmtD(g.latest)}).{' '}
            </span>
          ))}
          These predate the ERP becoming the book of record, so Busy owns them. Nothing here will ever
          mark them approved — a backdated approval would be a worse record than an honest gap.
        </div>
      )}

      {formRow && C.ApproveForm && <C.ApproveForm row={formRow} error={formErr} busy={busy === formRow.id}
        onCancel={() => setFormRow(null)} onSubmit={submitForm} />}
      {note && <div style={{ background: '#EAF7F1', border: '1px solid #BFE3D2', color: '#14684D',
                             borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 13 }}>{note}</div>}
      {err && <div style={{ background: '#FDECEC', border: '1px solid #F3C4C4', color: '#9B1C1C',
                            borderRadius: 8, padding: '9px 12px', marginBottom: 12, fontSize: 13 }}>{err}</div>}

      {loading ? <div style={{ padding: 20, color: '#888', fontSize: 13 }}>Loading…</div>
        : rows.length === 0 ? (
          <div style={{ padding: '26px 20px', textAlign: 'center', color: '#777', fontSize: 13.5 }}>
            {C.emptyText || `Nothing awaiting ${W.act.toLowerCase()}.`}
          </div>
        ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead><tr>
              {C.columns.map((c) => <th key={c.key} style={c.right ? { ...TH, textAlign: 'right' } : TH}>{c.label}</th>)}
              <th style={TH}>Raised by</th><th style={TH}>Waiting</th>
              {C.extraColumns && C.extraColumns.map((c) => <th key={c.key} style={TH}>{c.label}</th>)}
              <th style={{ ...TH, minWidth: 260 }}>{W.Verb}</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ background: r.can_certify ? '#fff' : '#FCFBF9' }}>
                  {C.columns.map((c) => (
                    <td key={c.key} style={c.right
                      ? { ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
                      : c.nowrap ? { ...TD, whiteSpace: 'nowrap' } : TD}>{c.render(r)}</td>
                  ))}
                  <td style={TD}>{r.created_by_name || <span style={{ color: '#9B1C1C' }}>not recorded</span>}</td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>{num(r.days_waiting)}d</td>
                  {C.extraColumns && C.extraColumns.map((c) => (
                    <td key={c.key} style={TD}>{c.render(r)}</td>
                  ))}
                  {/* THE REASON REPLACES THE BUTTONS. Not a greyed control with a tooltip: the
                      sentence occupies the space the button would have, so it cannot be missed. */}
                  <td style={TD}>
                    {r.can_certify ? (
                      <div style={{ display: 'flex', gap: 7 }}>
                        <button onClick={() => act(r, 'approved')} disabled={busy === r.id}
                          style={{ padding: '7px 13px', border: 'none', borderRadius: 6, cursor: 'pointer',
                                   background: '#1D9E75', color: '#fff', fontSize: 12.5, fontWeight: 600 }}>
                          {busy === r.id ? '…' : W.Verb}
                        </button>
                        <button onClick={() => act(r, 'rejected')} disabled={busy === r.id}
                          style={{ padding: '7px 13px', borderRadius: 6, cursor: 'pointer',
                                   background: '#fff', color: '#9B1C1C', border: '1px solid #E5B4B4', fontSize: 12.5 }}>
                          Reject
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: '#6B5418', background: '#FDF8EC',
                                    border: '1px solid #E8D9B0', borderRadius: 6, padding: '7px 9px', lineHeight: 1.45 }}>
                        {r.cannot_reason}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
